import {
  getDisplayCompletionPercentage,
  getTimeframeProgress,
} from '@griply/utils';
import { useMemo } from 'react';
import { useSchedulesPerfectSlots } from 'shared/hooks/use-schedules-perfect-slots';
import { useTimeframeEntries } from 'shared/hooks/use-timeframe-entries';
import { useTimeframeOccurrences } from 'shared/hooks/use-timeframe-occurrences';
import { HabitSchedule } from 'shared/types/habit-schedule';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { isNumber } from 'shared/utils/is-number';

export type Options = {
  startDate: Timestamp;
  endDate: Timestamp;
  weekStartsOn: WeekDays;
  today: Timestamp;
};

export const useWeekStatistics = (
  schedules: HabitSchedule[],
  { startDate, endDate, weekStartsOn, today }: Options,
) => {
  const { timeframeOccurrences } = useTimeframeOccurrences(schedules, {
    startDate,
    endDate,
  });
  const { dateEntries } = useTimeframeEntries(schedules, {
    startDate,
    endDate,
  });
  const [perfectWeek] = useSchedulesPerfectSlots(schedules, {
    startDate,
    endDate,
    weekStartsOn,
    strictDates: true,
    today,
  });

  const { completionsCount, target } = useMemo(
    () => getTimeframeProgress(schedules, { startDate, endDate }),
    [endDate, schedules, startDate],
  );

  const completionPercentage =
    isNumber(completionsCount) && isNumber(target)
      ? getDisplayCompletionPercentage(completionsCount!, target!)
      : undefined;

  return {
    occurrences: timeframeOccurrences,
    dateEntries,
    perfectWeek,
    completionsCount,
    target,
    completionPercentage,
  };
};
